import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Predictions.css';

function Predictions() {
  const { campeonatoId } = useParams();
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const getDayOfWeek = (dateString) => {
    const daysOfWeek = [
      'Domingo',
      'Segunda-feira',
      'Terça-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sábado',
    ];
    const date = new Date(dateString);
    return daysOfWeek[date.getDay()];
  };

  // Função para verificar se o jogo está encerrado
  const isMatchEnded = (matchDate) => {
    const matchDateTime = new Date(matchDate);
    const currentDateTime = new Date();
    return matchDateTime < currentDateTime;
  };

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/wp-json/palpitex/v1/matches`, {
          params: { campeonato_id: campeonatoId },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setMatches(response.data);
        setError(null);
      } catch (error) {
        setError(error.response?.data?.message || 'Erro ao carregar jogos.');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [campeonatoId]);

  const handleScoreChange = (matchId, team, value) => {
    setPredictions((prev) => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [team]: value >= 0 ? value : 0,
      },
    }));
  };

  const handleSubmit = async (matchId) => {
    const prediction = predictions[matchId];
    if (!prediction || prediction.score1 === undefined || prediction.score2 === undefined) {
      setError('Por favor, preencha os placares para o jogo.');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/wp-json/palpitex/v1/predictions`,
        {
          match_id: matchId,
          score1: prediction.score1,
          score2: prediction.score2,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      setSuccess(response.data.message);
      setMatches((prev) => prev.filter((match) => match.id !== matchId));
      setPredictions((prev) => {
        const newPredictions = { ...prev };
        delete newPredictions[matchId];
        return newPredictions;
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Erro ao enviar palpite.');
    }
  };

  const handleAddMatch = () => {
    navigate('/add-match');
  };

  return (
    <div className="predictions-container">
      <div className="predictions-header">
        <h2>Jogos para Palpitar</h2>
        {userRole === 'administrator' && (
          <button className="add-button" onClick={handleAddMatch}>
            +
          </button>
        )}
      </div>
      {loading && <p>Carregando jogos...</p>}
      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}
      {!loading && !error && matches.length === 0 && (
        <div className="no-matches">
          <p>Não há jogos disponíveis para palpitar neste campeonato.</p>
          <Link to="/my-predictions" className="my-predictions-link">
            Meus Palpites
          </Link>
        </div>
      )}
      {!loading && !error && matches.length > 0 && (
        <div className="predictions-list">
          {matches.map((match) => {
            const matchEnded = isMatchEnded(match.date);
            const isAdmin = userRole === 'administrator';
            return (
              <div key={match.id} className="match-item">
                <div className="match-teams">
                  <div className="team team-left">
                    <img src={match.escudo_time1} alt={match.team1} className="team-logo" />
                    <span>{match.team1}</span>
                  </div>
                  <div className="score-inputs">
                    <input
                      type="number"
                      min="0"
                      value={predictions[match.id]?.score1 ?? ''}
                      onChange={(e) => handleScoreChange(match.id, 'score1', parseInt(e.target.value))}
                      placeholder="Placar"
                      required
                      disabled={matchEnded || isAdmin} // Desativa se o jogo terminou ou se é administrador
                    />
                    <span className="vs">X</span>
                    <input
                      type="number"
                      min="0"
                      value={predictions[match.id]?.score2 ?? ''}
                      onChange={(e) => handleScoreChange(match.id, 'score2', parseInt(e.target.value))}
                      placeholder="Placar"
                      required
                      disabled={matchEnded || isAdmin} // Desativa se o jogo terminou ou se é administrador
                    />
                  </div>
                  <div className="team team-right">
                    <span>{match.team2}</span>
                    <img src={match.escudo_time2} alt={match.team2} className="team-logo" />
                  </div>
                </div>
                <div className="match-footer">
                  <span className="day-of-week">{`${getDayOfWeek(match.date)} - ${match.time}`}</span>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSubmit(match.id);
                    }}
                    className="submit-form"
                  >
                    <button
                      type="submit"
                      className="submit-button"
                      disabled={matchEnded || isAdmin} // Desativa o botão se o jogo terminou ou se é administrador
                    >
                      {matchEnded && !isAdmin ? 'Encerrado' : 'Palpite'}
                    </button>
                  </form>
                  <span className="match-date">{match.date}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Predictions;